import { useCallback, useEffect, useMemo, useState } from "react";
import Router from "next/router";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";

import { useAppSelector } from "@/redux/hooks";
import { logOutUser } from "@/services/auth";
import { axiosInstance, API_URL } from "@/services";
import { getPublicEnv } from "@/runtime-utils/config/public-env";
import {
  clearSessionDeadline,
  readSessionDeadline,
  SESSION_DEADLINE_EVENT,
} from "@/session/store";

const IDLE_MINUTES = Number(
  getPublicEnv("NEXT_PUBLIC_SESSION_IDLE_TIMEOUT_MINUTES", "0")
);
const COUNTDOWN_SECONDS = Number(
  getPublicEnv("NEXT_PUBLIC_SESSION_TIMEOUT_COUNTDOWN_SECONDS", "120")
);

function formatRemaining(milliseconds: number) {
  const seconds = Math.max(0, Math.ceil(milliseconds / 1000));
  if (seconds < 60) return `${seconds} seconds`;

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  const minuteLabel = `${minutes} ${minutes === 1 ? "minute" : "minutes"}`;

  return remainingSeconds > 0
    ? `${minuteLabel} ${remainingSeconds} seconds`
    : minuteLabel;
}

export default function SessionTimeout() {
  const theme = useTheme();
  const isLoggedIn = useAppSelector((state) => state.user.isLoggedIn);
  const [deadline, setDeadline] = useState<number | null>(null);
  const [lastActivity, setLastActivity] = useState(0);
  const [now, setNow] = useState(0);
  const countdownMilliseconds = useMemo(
    () =>
      (Number.isFinite(COUNTDOWN_SECONDS) && COUNTDOWN_SECONDS > 0
        ? COUNTDOWN_SECONDS
        : 120) * 1000,
    []
  );
  const idleTimeoutMilliseconds =
    Number.isFinite(IDLE_MINUTES) && IDLE_MINUTES > 0
      ? IDLE_MINUTES * 60_000
      : null;

  const endSession = useCallback(async () => {
    clearSessionDeadline();
    await logOutUser();
    await Router.push("/sign-in?reason=session-expired");
  }, []);

  useEffect(() => {
    const loadDeadline = () => {
      setDeadline(readSessionDeadline());
      setLastActivity(Date.now());
      setNow(Date.now());
    };
    loadDeadline();
    window.addEventListener(SESSION_DEADLINE_EVENT, loadDeadline);
    return () =>
      window.removeEventListener(SESSION_DEADLINE_EVENT, loadDeadline);
  }, []);

  const idleDeadline =
    idleTimeoutMilliseconds === null
      ? Number.POSITIVE_INFINITY
      : lastActivity + idleTimeoutMilliseconds;
  const expiresAt = deadline
    ? Math.min(deadline, idleDeadline + countdownMilliseconds)
    : null;
  const remaining = expiresAt ? expiresAt - now : null;
  const warningStartsAt = expiresAt ? expiresAt - countdownMilliseconds : null;
  const open = Boolean(
    isLoggedIn &&
    deadline &&
    warningStartsAt !== null &&
    now >= warningStartsAt &&
    remaining !== null &&
    remaining > 0
  );

  useEffect(() => {
    if (!isLoggedIn || open) return;
    const updateActivity = () => setLastActivity(Date.now());
    for (const event of ["pointerdown", "keydown", "touchstart"]) {
      window.addEventListener(event, updateActivity, { passive: true });
    }
    return () => {
      for (const event of ["pointerdown", "keydown", "touchstart"]) {
        window.removeEventListener(event, updateActivity);
      }
    };
  }, [isLoggedIn, open]);

  useEffect(() => {
    if (!isLoggedIn || !deadline) return;
    const millisecondsUntilWarning = Math.max(
      0,
      (warningStartsAt ?? Number.POSITIVE_INFINITY) - now
    );
    const delay =
      open || (remaining ?? Number.POSITIVE_INFINITY) <= 60_000
        ? 1_000
        : Math.min(60_000, millisecondsUntilWarning);
    const timer = window.setInterval(() => setNow(Date.now()), delay);
    return () => window.clearInterval(timer);
  }, [deadline, isLoggedIn, now, open, remaining, warningStartsAt]);

  useEffect(() => {
    if (!isLoggedIn || !deadline) return;

    const syncClock = () => setNow(Date.now());
    const syncVisibleClock = () => {
      if (document.visibilityState === "visible") syncClock();
    };

    window.addEventListener("focus", syncClock);
    document.addEventListener("visibilitychange", syncVisibleClock);
    return () => {
      window.removeEventListener("focus", syncClock);
      document.removeEventListener("visibilitychange", syncVisibleClock);
    };
  }, [deadline, isLoggedIn]);

  useEffect(() => {
    if (isLoggedIn && remaining !== null && remaining <= 0) endSession();
  }, [endSession, isLoggedIn, remaining]);

  const continueSession = async () => {
    try {
      await axiosInstance.post(`${API_URL}/tenant/auth/session/renew`);
      setLastActivity(Date.now());
    } catch {
      endSession();
    }
  };

  if (!open || remaining === null) return null;
  const seconds = Math.ceil(remaining / 1000);
  const announcement =
    seconds > 60
      ? ""
      : seconds > 40
        ? "1 minute remaining"
        : seconds > 20
          ? "40 seconds remaining"
          : seconds > 0
            ? "20 seconds remaining"
            : "Session timed out";
  return (
    <Dialog
      open
      aria-labelledby="session-timeout-title"
      maxWidth="xs"
      fullWidth
      slotProps={{ paper: { sx: { borderRadius: 3 } } }}
    >
      <DialogTitle id="session-timeout-title" sx={{ pb: 1 }}>
        Your session is about to expire
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Typography
          data-testid="session-countdown"
          variant="body1"
          style={{ minHeight: "2.7em", whiteSpace: "normal" }}
          sx={{
            fontSize: "1rem",
            lineHeight: 1.5,
          }}
        >
          Your session will end in {formatRemaining(remaining)}.
        </Typography>
        <Typography
          role="status"
          aria-live="polite"
          sx={{
            position: "absolute",
            width: 1,
            height: 1,
            overflow: "hidden",
            clip: "rect(0 0 0 0)",
          }}
        >
          {announcement}
        </Typography>
        <Alert severity="info" sx={{ mt: 3, borderRadius: 2 }}>
          Select “Continue session” to stay signed in.
        </Alert>
      </DialogContent>
      <DialogActions
        disableSpacing
        sx={{
          px: 3,
          pb: 3,
          pt: 1,
          gap: 1.5,
          flexDirection: { xs: "column-reverse", sm: "row" },
          alignItems: "stretch",
        }}
      >
        <Button
          variant="outlined"
          color="primary"
          onClick={endSession}
          sx={{
            width: "100%",
            minWidth: { sm: 132 },
            minHeight: 52,
            borderRadius: 2,
            textTransform: "none",
            whiteSpace: "nowrap",
            fontWeight: 600,
            [theme.breakpoints.up("sm")]: {
              width: "auto",
            },
          }}
        >
          Sign out
        </Button>
        <Button
          variant="contained"
          onClick={() => void continueSession()}
          sx={{
            width: "100%",
            flex: "0 0 auto",
            minHeight: 52,
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 600,
            [theme.breakpoints.up("sm")]: {
              width: "auto",
              flex: 1,
            },
          }}
        >
          Continue session
        </Button>
      </DialogActions>
    </Dialog>
  );
}
